import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Copy, Crown, Share2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referrals/me")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(label || t("referrals.copied"));
  };

  if (loading || !data) {
    return <div className="text-center text-white/40 font-mono py-32 tracking-widest text-xs">{t("common.loading")}</div>;
  }

  const inviteLink = `${window.location.origin}/register?ref=${data.referral_code}`;
  const progress = Math.min(100, (data.premium_referrals_count / data.threshold) * 100);

  return (
    <div className="page-enter max-w-[1100px] mx-auto px-6 lg:px-10 py-12">
      <div className="mb-10">
        <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-2">{t("referrals.label")}</div>
        <h1 className="font-heading font-black text-5xl tracking-tighter">{t("referrals.title")}</h1>
        <p className="text-white/60 mt-3 max-w-2xl leading-relaxed">
          {t("referrals.subtitle", { threshold: data.threshold, pct: data.discount_pct })}
        </p>
      </div>

      <div className="grid lg:grid-cols-[1.3fr,1fr] gap-6 mb-12">
        {/* Code + Share */}
        <div className="flat-card p-8">
          <div className="font-mono text-xs text-white/50 uppercase tracking-widest mb-3">{t("referrals.your_code")}</div>
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <div className="font-mono font-bold text-3xl md:text-4xl tracking-widest text-[#FACC15] neon-yellow" data-testid="referral-code-display">{data.referral_code}</div>
            <button
              onClick={() => copy(data.referral_code)}
              data-testid="copy-code-btn"
              className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5"
            >
              <Copy className="w-3.5 h-3.5" /> {t("referrals.copy")}
            </button>
          </div>
          <div className="font-mono text-xs text-white/50 uppercase tracking-widest mb-2 mt-6">{t("referrals.share_link")}</div>
          <div className="flex items-center gap-2 bg-black/40 border border-white/10 px-3 py-2 font-mono text-xs text-white/70 overflow-hidden">
            <Share2 className="w-3.5 h-3.5 text-[#00E5FF] flex-shrink-0" />
            <span className="truncate flex-1" data-testid="referral-link">{inviteLink}</span>
            <button onClick={() => copy(inviteLink)} data-testid="copy-link-btn" className="text-white/60 hover:text-[#FACC15] transition flex-shrink-0">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className={`flat-card p-8 ${data.referral_discount_active ? "border-[#FACC15]/50" : ""}`} data-testid="referral-progress-card">
          {data.referral_discount_active ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <Crown className="w-5 h-5 text-[#FACC15]" />
                <div className="font-heading font-bold text-xl tracking-tight">{t("referrals.discount_unlocked_title")}</div>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                {t("referrals.discount_unlocked_desc", { pct: data.discount_pct })}
              </p>
              <div className="mt-5 inline-flex items-center gap-2 px-3 py-1 bg-[#FACC15] text-black font-mono text-xs font-bold uppercase tracking-widest">
                <Check className="w-3.5 h-3.5" /> -{data.discount_pct}%
              </div>
            </>
          ) : (
            <>
              <div className="font-mono text-xs text-white/50 uppercase tracking-widest mb-3">{t("referrals.progress")}</div>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-heading font-black text-5xl tracking-tighter">{data.premium_referrals_count}</span>
                <span className="font-mono text-white/40 text-sm">{t("referrals.of")} {data.threshold}</span>
              </div>
              <div className="h-2 bg-white/5 overflow-hidden">
                <div className="h-full bg-[#FACC15] transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-white/50 text-xs mt-3 font-mono uppercase tracking-wider">
                <Sparkles className="w-3 h-3 inline mr-1" /> -{data.discount_pct}% UNLOCK AT {data.threshold}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Invitees */}
      <div>
        <div className="font-mono text-xs text-white/50 uppercase tracking-widest mb-3">{t("referrals.your_invites")} ({data.total_referred})</div>
        {data.referred.length === 0 ? (
          <div className="flat-card p-10 text-center text-white/50 text-sm" data-testid="empty-invites">
            {t("referrals.no_invites")}
          </div>
        ) : (
          <div className="flat-card overflow-hidden">
            <ul className="divide-y divide-white/10">
              {data.referred.map((r, i) => (
                <li key={i} className="flex items-center justify-between px-5 py-3 text-sm">
                  <div>
                    <div className="font-heading font-semibold">{r.name}</div>
                    <div className="font-mono text-[10px] text-white/40 tracking-wider">
                      {t("referrals.joined")} {r.joined_at?.slice(0, 10)}
                    </div>
                  </div>
                  <span className={`lang-chip ${r.is_premium ? "text-[#FACC15] border-[#FACC15]/40" : ""}`}>
                    {r.is_premium && <Crown className="w-3 h-3" />}
                    {r.is_premium ? t("referrals.is_premium") : t("referrals.is_free")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
